import React, { useState, useRef, useEffect } from "react";
import { useTranslation } from "react-i18next";
import DOMPurify from "dompurify";
import fusoChat from "../../assets/fusoChat.svg";
import SimpleFeedbackModal from "../Modals/SimpleFeedbackModal";
import "../tooltipStyles.css";
import "./FusoChatMessage.css";

const FusoChatMessage = ({
  message,
  index,
  handleFeedback,
  dots,
  formatResponse,
  key,
  cycleId,
  pushAskAnotherQuery,
  handleResetQuery,
  isHistoryView = false,
  userId = null,
}) => {
  const { t, i18n } = useTranslation();
  const [buttonsHidden, setButtonsHidden] = useState(
    message?.hideFeedbackButtons || false
  );
  const [feedbackGiven, setFeedbackGiven] = useState(false);
  const [showActionButtons, setShowActionButtons] = useState(false);
  const [copySuccess, setCopySuccess] = useState(false);
  const [isSpeaking, setIsSpeaking] = useState(false);
  const speechSynthRef = useRef(window.speechSynthesis);
  const [showFeedbackModal, setShowFeedbackModal] = useState(false);
  const [feedbackType, setFeedbackType] = useState(null);
  const [selectedChatRecordId, setSelectedChatRecordId] = useState(null);
  const [showToast, setShowToast] = useState(false);
  const [toastMessage, setToastMessage] = useState("");
  // New state for tooltips
  const [showGoodTooltip, setShowGoodTooltip] = useState(false);
  const [showBadTooltip, setShowBadTooltip] = useState(false);
  const [showCopyTooltip, setShowCopyTooltip] = useState(false);
  const [showLoudTooltip, setShowLoudTooltip] = useState(false);

  // New state for image modal
  const [showImageModal, setShowImageModal] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null);
  const [modalImageIndex, setModalImageIndex] = useState(0);

  // Refs for image handling
  const imageContainerRef = useRef(null);
  const imageRef = useRef(null);

  // State for step-by-step zoom functionality
  const [zoomLevel, setZoomLevel] = useState(1); // 1 = 100%, 1.25 = 125%, 1.5 = 150%, etc.
  const minZoom = 0.5; // 50%
  const maxZoom = 3; // 300%
  const zoomStep = 0.25; // 25% increment

  // State for drag-to-scroll functionality
  const [isDragging, setIsDragging] = useState(false);
  const [startX, setStartX] = useState(0);
  const [startY, setStartY] = useState(0);
  const [scrollLeft, setScrollLeft] = useState(0);
  const [scrollTop, setScrollTop] = useState(0);

  const [thumbsUpClicked, setThumbsUpClicked] = useState(
    message?.user_feedback === 1
  );
  const [thumbsDownClicked, setThumbsDownClicked] = useState(
    message?.user_feedback === -1
  );

  useEffect(() => {
    if (message?.hideFeedbackButtons) {
      setButtonsHidden(true);
    }
  }, [message?.hideFeedbackButtons]);

  // Handle escape key to close modal
  useEffect(() => {
    const handleEscKey = (event) => {
      if (event.key === 'Escape' && showImageModal) {
        setShowImageModal(false);
      }
    };

    if (showImageModal) {
      document.addEventListener('keydown', handleEscKey);
      // Prevent body scroll when modal is open
      document.body.style.overflow = 'hidden';
    }

    return () => {
      document.removeEventListener('keydown', handleEscKey);
      document.body.style.overflow = 'unset';
    };
  }, [showImageModal]);

  const enhancedFormatResponse = (text) => {
    if (!text) return "";

    let formattedText = formatResponse ? formatResponse(text) : text;

    if (!formatResponse) {
      formattedText = text
        .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
        .replace(/\n/g, "<br/>");
    }

    // Wrap complete table elements with horizontal scroll container
    formattedText = formattedText.replace(
      /<table([^>]*?)>(.*?)<\/table>/gs,
      '<div class="table-wrapper overflow-x-auto w-full thin-scrollbar"><table class="response-table tableWidthPnc"$1>$2</table></div>'
    );

    return formattedText;
  };

  const handleFeedbackClick = (response) => {
    // Hide the entire feedback section when clicked
    setFeedbackGiven(true);
    // Call the parent handler
    handleFeedback(index, response);
  };

  useEffect(() => {
    if (message?.user_feedback === 1) {
      setThumbsUpClicked(true);
      setThumbsDownClicked(false);
    } else if (message?.user_feedback === -1) {
      setThumbsUpClicked(false);
      setThumbsDownClicked(true);
    } else {
      setThumbsUpClicked(false);
      setThumbsDownClicked(false);
    }
  }, [message?.user_feedback]);

  useEffect(() => {
    return () => {
      if (speechSynthRef.current) {
        speechSynthRef.current.cancel();
      }
    };
  }, []);

  const getChatRecordId = () => {
    let chatRecordId = message.chat_record_id;

    if (!chatRecordId) {
      chatRecordId = message.chatId || message.messageId;

      if (!chatRecordId && typeof message === "object") {
        for (const key in message) {
          if (message[key] && message[key].chat_record_id) {
            chatRecordId = message[key].chat_record_id;
            break;
          }
        }
      }
    }

    if (!chatRecordId) {
      console.error("Missing chat_record_id in message", message);
      return null;
    }

    return chatRecordId;
  };

  const handleThumbsUp = () => {
    const chatRecordId = getChatRecordId();
    if (!chatRecordId) return;

    setFeedbackType("positive");
    setSelectedChatRecordId(chatRecordId);
    setShowFeedbackModal(true);
  };

  const handleThumbsDown = () => {
    const chatRecordId = getChatRecordId();
    if (!chatRecordId) return;
    setFeedbackType("negative");
    setSelectedChatRecordId(chatRecordId);
    setShowFeedbackModal(true);
  };

  const handleCloseFeedbackModal = () => {
    setShowFeedbackModal(false);
    setSelectedChatRecordId(null);
  };

  const handleFeedbackSubmit = (textFeedback, success = false) => {
    if (success) {
      if (feedbackType === "positive") {
        setThumbsUpClicked(true);
        setThumbsDownClicked(false);
      } else if (feedbackType === "negative") {
        setThumbsDownClicked(true);
        setThumbsUpClicked(false);
      }
    }

    handleCloseFeedbackModal();
  };

  const handleCopy = () => {
    const messageText = message.text.replace(/<[^>]*>/g, "");

    navigator.clipboard
      .writeText(messageText)
      .then(() => {
        setCopySuccess(true);
        setTimeout(() => {
          setCopySuccess(false);
        }, 2000);
      })
      .catch((err) => {
        console.error("Failed to copy message:", err);
      });
  };

  const handleSound = () => {
    if (isSpeaking) {
      speechSynthRef.current.cancel();
      setIsSpeaking(false);
      return;
    }

    const messageText = message.text.replace(/<[^>]*>/g, "");

    const utterance = new SpeechSynthesisUtterance(messageText);

    utterance.onstart = () => {
      setIsSpeaking(true);
    };

    utterance.onend = () => {
      setIsSpeaking(false);
    };

    utterance.onerror = (event) => {
      setIsSpeaking(false);
      console.error("Speech error:", event.error);
    };

    // Start speaking
    speechSynthRef.current.speak(utterance);
  };

  // New function to handle PDF download
  const handlePdfDownload = (pdfUrl) => {
    try {
      // Extract filename from URL or use a default name
      const urlParts = pdfUrl.split('/');
      const fileName = urlParts[urlParts.length - 1].split('?')[0] || 'document.pdf';

      // Create a temporary anchor element to trigger download
      const link = document.createElement('a');
      link.href = pdfUrl;
      link.download = fileName;
      link.target = '_blank';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (error) {
      console.error('Error downloading PDF:', error);
      // Fallback: open in new tab
      window.open(pdfUrl, '_blank');
    }
  };

  // FIXED: Enhanced function to handle image click for modal with security bypass
  const handleImageClick = (imageUrl, imgIndex) => {
    // Set security bypass flags to prevent logout
    sessionStorage.setItem('legitimate_modal_interaction', 'true');
    sessionStorage.setItem('modal_interaction_timestamp', Date.now().toString());
    sessionStorage.setItem('legitimate_navigation', 'true');
    
    // Set modal state
    setSelectedImage(imageUrl);
    setModalImageIndex(imgIndex);
    setShowImageModal(true);
  };

  // FIXED: Function to close modal with proper cleanup
  const handleCloseModal = () => {
    // Set security bypass flags before closing
    sessionStorage.setItem('legitimate_modal_interaction', 'true');
    sessionStorage.setItem('modal_close_timestamp', Date.now().toString());

    setShowImageModal(false);
    setSelectedImage(null);
    setZoomLevel(1); // Reset zoom level when closing modal

    // Clean up security flags after a short delay
    setTimeout(() => {
      sessionStorage.removeItem('legitimate_modal_interaction');
      sessionStorage.removeItem('modal_interaction_timestamp');
      sessionStorage.removeItem('modal_close_timestamp');
    }, 500);

  };

  // FIXED: Function to navigate between images in modal with security bypass
  const handlePrevImage = () => {
    if (message.imageLinks && message.imageLinks.length > 1) {
      // Set security bypass flag for navigation
      sessionStorage.setItem('legitimate_modal_interaction', 'true');

      const prevIndex = modalImageIndex > 0 ? modalImageIndex - 1 : message.imageLinks.length - 1;
      setModalImageIndex(prevIndex);
      setSelectedImage(message.imageLinks[prevIndex]);
      setZoomLevel(1); // Reset zoom level when switching images
    }
  };

  const handleNextImage = () => {
    if (message.imageLinks && message.imageLinks.length > 1) {
      // Set security bypass flag for navigation
      sessionStorage.setItem('legitimate_modal_interaction', 'true');

      const nextIndex = modalImageIndex < message.imageLinks.length - 1 ? modalImageIndex + 1 : 0;
      setModalImageIndex(nextIndex);
      setSelectedImage(message.imageLinks[nextIndex]);
      setZoomLevel(1); // Reset zoom level when switching images
    }
  };

  // Zoom functions
  const handleZoomIn = () => {
    setZoomLevel(prev => Math.min(prev + zoomStep, maxZoom));
  };

  const handleZoomOut = () => {
    setZoomLevel(prev => Math.max(prev - zoomStep, minZoom));
  };

  const handleResetZoom = () => {
    setZoomLevel(1);
  };

  // Simple image load handler
  const handleImageLoad = (e) => {
    // Image loaded
  };

  // Drag-to-scroll handlers
  const handleMouseDown = (e) => {
    if (zoomLevel <= 1 || !imageContainerRef.current) return;
    setIsDragging(true);
    setStartX(e.pageX - imageContainerRef.current.offsetLeft);
    setStartY(e.pageY - imageContainerRef.current.offsetTop);
    setScrollLeft(imageContainerRef.current.scrollLeft);
    setScrollTop(imageContainerRef.current.scrollTop);
  };

  const handleMouseMove = (e) => {
    if (!isDragging || !imageContainerRef.current) return;
    e.preventDefault();
    const x = e.pageX - imageContainerRef.current.offsetLeft;
    const y = e.pageY - imageContainerRef.current.offsetTop;
    const walkX = (x - startX) * 1.5; // Multiply for faster scroll
    const walkY = (y - startY) * 1.5;
    imageContainerRef.current.scrollLeft = scrollLeft - walkX;
    imageContainerRef.current.scrollTop = scrollTop - walkY;
  };

  const handleMouseUp = () => {
    setIsDragging(false);
  };

  const handleMouseLeave = () => {
    setIsDragging(false);
  };

  // FIXED: Add effect to handle modal state changes and set security flags
  useEffect(() => {
    if (showImageModal) {
      // Set security bypass flags when modal opens
      sessionStorage.setItem('legitimate_modal_interaction', 'true');
      sessionStorage.setItem('modal_open', 'true');
      sessionStorage.setItem('modal_open_timestamp', Date.now().toString());
      
      // Prevent body scroll and focus management
      document.body.style.overflow = 'hidden';
    } else {
      // Clean up when modal closes
      setTimeout(() => {
        sessionStorage.removeItem('modal_open');
        sessionStorage.removeItem('legitimate_modal_interaction');
        sessionStorage.removeItem('modal_open_timestamp');
      }, 300);
      
      document.body.style.overflow = 'unset';
    }

    return () => {
      // Cleanup on unmount
      if (showImageModal) {
        document.body.style.overflow = 'unset';
        sessionStorage.removeItem('modal_open');
        sessionStorage.removeItem('legitimate_modal_interaction');
      }
    };
  }, [showImageModal]);

  if (!message) return null;

  // Check if spn_url is anywhere in the message object (nested search)
  const findSpnUrl = (obj, path = '') => {
    for (let key in obj) {
      if (obj.hasOwnProperty(key)) {
        const currentPath = path ? `${path}.${key}` : key;
        if (key === 'spn_url' && obj[key]) {
          // console.log(`Found spn_url at: ${currentPath} = ${obj[key]}`);
        }
        if (typeof obj[key] === 'object' && obj[key] !== null) {
          findSpnUrl(obj[key], currentPath);
        }
      }
    }
  };
  findSpnUrl(message);

  const tooltipContainerStyle = {
    position: "relative",
    display: "inline-block",
  };

  return (
    <>
      <div
        key={`${cycleId}-fuso-message-${index}`}
        id={message.messageId}
        data-message-id={message.messageId}
        className="fusoChatFirst mt-[16px] msgPartPnc"
        onMouseEnter={() => setShowActionButtons(true)}
        onMouseLeave={() => setShowActionButtons(false)}
      >
        <div className="aiResponsePnc">
          <div className="flex gap-3">
            <img src={fusoChat} alt="FUSO" className="w-6 h-6 fusoLogoSmall" />
            <div className="bg-red-50 rounded-lg p-4 pncChatFirst assistanceResponse pncAssistanceResponse">
              <div className="flex gap-3 tableResPnc">
                <div className="flex-1">
                  <div
                    className="assisantAnswerPnc"
                    dangerouslySetInnerHTML={{
                      __html: DOMPurify.sanitize(enhancedFormatResponse(message.text)),
                    }}
                  />
                  {message.isLoading && (
                    <p>
                      {t("thinking")}{" "}
                      <span className="dots-animation">{dots}</span>
                    </p>
                  )}
                  {message.imageLinks?.length > 0 && (
                    <div className="mt-3">
                      {/* Reduced gap from gap-3 to gap-1 for tighter spacing */}
                      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-2">
                        {message.imageLinks.map((imageUrl, imgIndex) => (
                          <div
                            key={`${cycleId}-img-${index}-${imgIndex}`}
                            className="relative overflow-hidden rounded-lg flex items-center justify-center cursor-zoom-in group border border-gray-200 hover:border-gray-300 transition-all duration-200 shadow-md hover:shadow-lg"
                            style={{
                              maxHeight: "425px",
                              width: "80%"
                            }}
                            onClick={() => handleImageClick(imageUrl, imgIndex)}
                          >
                            <img
                              src={imageUrl}
                              alt={`Assistant image ${imgIndex + 1}`}
                              className="w-full h-full object-cover transition-opacity duration-200 group-hover:opacity-80"
                              loading="lazy"
                            />
                            {/* Simple overlay to indicate clickable */}
                            <div className="absolute inset-0 bg-opacity-0 group-hover:bg-opacity-20 transition-all duration-200 flex items-center justify-center">
                              <div className="bg-white bg-opacity-90 rounded-full p-2 opacity-0 group-hover:opacity-100 transition-opacity duration-200">
                                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                  <path d="M15 3h6v6" />
                                  <path d="M10 14 21 3" />
                                  <path d="M18 13v6a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V8a2 2 0 0 1 2-2h6" />
                                </svg>
                              </div>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {/* PDF Download Section - Multiple ways to check for PDF */}
                  {(message.spn_url || message.spnUrl || message.pdf_url || message.pdfUrl) && (
                    <div className="pdf-section pdfPncNew">
                      <div className="pdf-section-title">
                        {/* {t("attachedDocument") || "Attached Document"} */}
                      </div>
                      <button
                        onClick={() => handlePdfDownload(message.spn_url || message.spnUrl || message.pdf_url || message.pdfUrl)}
                        className="pdf-download-btn"
                        title="Download PDF Document"
                      >
                        <svg
                          xmlns="http://www.w3.org/2000/svg"
                          width="16"
                          height="16"
                          viewBox="0 0 24 24"
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          className="ml-1 mr-2"
                        >
                          <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
                          <polyline points="7 10 12 15 17 10"></polyline>
                          <line x1="12" y1="15" x2="12" y2="3"></line>
                        </svg>
                        {t("Download PDF") || "Download SPN Document"}
                      </button>
                    </div>
                  )}
                </div>
              </div>

              {!isHistoryView && !feedbackGiven && !buttonsHidden && !message.hideFeedbackButtons && (
                <div className="mt-2">
                  <>
                    <div>{t("gladMsg")}</div>
                    <div className="flex gap-4 mt-2">
                      <button
                        className="px-4 py-2 bg-[transparent] rounded yesBtnPnc"
                        onClick={() => handleFeedbackClick("Yes")}
                      >
                        {t("yes")}
                      </button>
                      <button
                        className="px-4 py-2 bg-[transparent] rounded yesBtnPnc"
                        onClick={() => handleFeedbackClick("No")}
                      >
                        {t("no")}
                      </button>
                    </div>
                  </>
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Action buttons outside the red background div */}
        <div
          className={`mt-2 flex gap-2 selectBtnPnc mb-[3rem] ${showActionButtons ? "opacity-100" : "opacity-0"
            } transition-opacity duration-200`}
        >
          {/* Thumbs Up with tooltip */}
          <div className="tooltipContainerStyle">
            <button
              onClick={handleThumbsUp}
              className={`p-2 hover:bg-gray-100 rounded-full focus:outline-none actionBtnPnc ${thumbsUpClicked ? "" : ""
                }`}
              aria-label="Thumbs up"
              onMouseEnter={() => setShowGoodTooltip(true)}
              onMouseLeave={() => setShowGoodTooltip(false)}
            >
              {thumbsUpClicked ? (
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                >
                  <path
                    d="M7.23781 11V20H5.62781C4.72781 20 4.00781 19.28 4.00781 18.39V12.62C4.00781 11.73 4.73781 11 5.62781 11H7.23781ZM18.4978 9.5H13.7178V6C13.7178 4.9 12.8178 4 11.7278 4H11.6378C11.2378 4 10.8778 4.24 10.7178 4.61L7.98781 11V20H17.1878C17.9178 20 18.5378 19.48 18.6678 18.76L19.9878 11.26C20.1478 10.34 19.4478 9.5 18.5078 9.5H18.4978Z"
                    fill="black"
                  />
                </svg>
              ) : (
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                >
                  <path
                    d="M3.00576 10C3.00576 9.44772 3.45347 9 4.00576 9L7.00576 9L7.00576 21L4.00576 21C3.45347 21 3.00576 20.5523 3.00576 20L3.00576 10Z"
                    stroke="black"
                    strokeWidth="1.5"
                  />
                  <path
                    d="M9.24998 20.4962L7.00576 19L7.00576 11L7.89715 11C8.27099 11 8.61361 10.7915 8.78537 10.4594L11.7176 4.79038C12.5006 3.27664 14.8004 4.03366 14.5311 5.7165L14.191 7.84201C14.0938 8.44959 14.5632 9 15.1785 9L18.5461 9C19.8148 9 20.7629 10.1659 20.5041 11.4079L19.0033 18.6119C18.7135 20.003 17.4874 21 16.0663 21L10.9141 21C10.3218 21 9.74278 20.8247 9.24998 20.4962Z"
                    stroke="black"
                    strokeWidth="1.5"
                  />
                </svg>
              )}
            </button>
            {showGoodTooltip && (
              <div className="tooltipStyle">
                {t("good")}
                <div className="tooltipArrowStyle"></div>
              </div>
            )}
          </div>

          {/* Thumbs Down with tooltip */}
          <div className="tooltipContainerStyle">
            <button
              onClick={handleThumbsDown}
              className={`p-2 hover:bg-gray-100 rounded-full focus:outline-none actionBtnPnc ${thumbsDownClicked ? "" : ""
                }`}
              aria-label="Thumbs down"
              onMouseEnter={() => setShowBadTooltip(true)}
              onMouseLeave={() => setShowBadTooltip(false)}
            >
              {thumbsDownClicked ? (
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                >
                  <path
                    d="M16.7622 13V4H18.3722C19.2722 4 19.9922 4.72 19.9922 5.61V11.38C19.9922 12.27 19.2622 13 18.3722 13H16.7622ZM5.50219 14.5H10.2822V18C10.2822 19.1 11.1822 20 12.2722 20H12.3622C12.7622 20 13.1222 19.76 13.2822 19.39L16.0122 13V4H6.81219C6.08219 4 5.46219 4.52 5.33219 5.24L4.01219 12.74C3.85219 13.66 4.55219 14.5 5.49219 14.5H5.50219Z"
                    fill="black"
                  />
                </svg>
              ) : (
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                >
                  <path
                    d="M20.9942 14C20.9942 14.5523 20.5465 15 19.9942 15H16.9942V3H19.9942C20.5465 3 20.9942 3.44772 20.9942 4V14Z"
                    stroke="black"
                    strokeWidth="1.5"
                  />
                  <path
                    d="M14.75 3.50385L16.9942 5V13H16.1029C15.729 13 15.3864 13.2085 15.2146 13.5406L12.2824 19.2096C11.4994 20.7234 9.19963 19.9663 9.46888 18.2835L9.80897 16.158C9.90618 15.5504 9.43684 15 8.82153 15H5.45385C4.18523 15 3.23715 13.8341 3.49589 12.5921L4.99672 5.38814C5.28654 3.99698 6.51263 3 7.93366 3H13.0859C13.6782 3 14.2572 3.17531 14.75 3.50385Z"
                    stroke="black"
                    strokeWidth="1.5"
                  />
                </svg>
              )}
            </button>
            {showBadTooltip && (
              <div className="tooltipStyle">
                {t("bad")}
                <div className="tooltipArrowStyle"></div>
              </div>
            )}
          </div>

          <div className="tooltipContainerStyle">
            <button
              onClick={handleCopy}
              className="p-2 hover:bg-gray-100 rounded-full focus:outline-none actionBtnPnc"
              aria-label="Copy message"
              onMouseEnter={() => setShowCopyTooltip(true)}
              onMouseLeave={() => setShowCopyTooltip(false)}
            >
              {copySuccess ? (
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                >
                  <path
                    d="M5 13L9 17L19 7"
                    stroke="#6B7280"
                    strokeWidth="2"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              ) : (
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                >
                  <path
                    d="M9.99219 6.49609V5.49609C9.99219 4.39152 10.8876 3.49609 11.9922 3.49609H18.4922C19.5968 3.49609 20.4922 4.39152 20.4922 5.49609V11.9961C20.4922 13.1007 19.5968 13.9961 18.4922 13.9961H17.4922M5.99219 19.9961H12.9922C14.0968 19.9961 14.9922 19.1007 14.9922 17.9961V10.9961C14.9922 9.89152 14.0968 8.99609 12.9922 8.99609H5.99219C4.88762 8.99609 3.99219 9.89152 3.99219 10.9961V17.9961C3.99219 19.1007 4.88762 19.9961 5.99219 19.9961Z"
                    stroke="black"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                  />
                </svg>
              )}
            </button>
            {showCopyTooltip && (
              <div className="tooltipStyle">
                {t("copy")}
                <div className="tooltipArrowStyle"></div>
              </div>
            )}
          </div>

          <div className="tooltipContainerStyle">
            <button
              onClick={handleSound}
              className={`p-2 hover:bg-gray-100 rounded-full focus:outline-none actionBtnPnc ${isSpeaking ? "bg-blue-100" : ""
                }`}
              aria-label={isSpeaking ? "Stop speaking" : "Read aloud"}
              onMouseEnter={() => setShowLoudTooltip(true)}
              onMouseLeave={() => setShowLoudTooltip(false)}
            >
              {isSpeaking ? (
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="black"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <rect x="6" y="4" width="4" height="16" rx="1" />
                  <rect x="14" y="4" width="4" height="16" rx="1" />
                </svg>
              ) : (
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  width="24"
                  height="24"
                  viewBox="0 0 24 24"
                  fill="none"
                >
                  <path
                    d="M15.8302 15.2094C16.5435 14.3594 16.9537 13.2962 16.9963 12.1874C17.0389 11.0786 16.7114 9.9871 16.0655 9.08486M18.8944 17.7806C20.2406 16.1762 20.9852 14.1526 20.9998 12.0583C21.0144 9.96402 20.2982 7.9302 18.9745 6.30721M3 9.99547V13.9955C3 14.5478 3.44772 14.9955 4 14.9955H6.64922C6.87629 14.9955 7.0966 15.0728 7.27391 15.2146L10.3753 17.6957C11.0301 18.2195 12 17.7534 12 16.9148V7.0761C12 6.23759 11.0301 5.77142 10.3753 6.29523L7.27391 8.77634C7.0966 8.91819 6.87629 8.99547 6.64922 8.99547H4C3.44772 8.99547 3 9.44319 3 9.99547Z"
                    stroke="black"
                    strokeWidth="1.5"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
              )}
            </button>
            {showLoudTooltip && (
              <div className="tooltipStyle">
                {t("readAloud")}
                <div className="tooltipArrowStyle"></div>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* FIXED: Image Modal with enhanced security bypass and proper event handling */}
      {showImageModal && selectedImage && (
        <div
          className="fixed inset-0 bg-gray-500 bg-opacity-90 flex items-center justify-center z-50 mt-0"
          onClick={handleCloseModal}
          onFocus={(e) => {
            // Prevent focus loss detection by security system
            sessionStorage.setItem('legitimate_modal_interaction', 'true');
          }}
          onBlur={(e) => {
            // Prevent blur detection by security system
            sessionStorage.setItem('legitimate_modal_interaction', 'true');
          }}
        >
          <div
            className="relative flex items-center justify-center imageSmallRes"
            onClick={(e) => {
              e.stopPropagation();
              // Set security bypass flag on any interaction
              sessionStorage.setItem('legitimate_modal_interaction', 'true');
            }}
            style={{
              // height: '97vh',
              padding: '10px',
              position: 'relative'
            }}
          >
            {/* Hidden scrollbar styles */}
            <style jsx>{`
              .hidden-scrollbar::-webkit-scrollbar {
                width: 0px;
                height: 0px;
                background: transparent;
              }
              .hidden-scrollbar {
                -ms-overflow-style: none;  /* IE and Edge */
                scrollbar-width: none;  /* Firefox */
              }
            `}</style>

            {/* Main image container - with step-by-step zoom support */}
            <div
              ref={imageContainerRef}
              className={`relative rounded-lg bg-white hidden-scrollbar ${zoomLevel > 1 ? 'overflow-auto' : 'overflow-visible'}`}
              style={{
                height: 'auto',
                maxHeight: '100%',
                width: 'auto',
                maxWidth: '100%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: zoomLevel > 1 ? (isDragging ? 'grabbing' : 'grab') : 'default',
              }}
              onFocus={(e) => {
                sessionStorage.setItem('legitimate_modal_interaction', 'true');
              }}
              onMouseDown={handleMouseDown}
              onMouseMove={handleMouseMove}
              onMouseUp={handleMouseUp}
              onMouseLeave={handleMouseLeave}
            >
              <div
                style={{
                  width: zoomLevel > 1 ? `${100 * zoomLevel}%` : 'auto',
                  height: zoomLevel > 1 ? `${100 * zoomLevel}%` : 'auto',
                  display: 'flex',
                  alignItems: zoomLevel > 1 ? 'flex-start' : 'center',
                  justifyContent: zoomLevel > 1 ? 'flex-start' : 'center',
                }}
              >
                <img
                  ref={imageRef}
                  src={selectedImage}
                  alt={`Full size image ${modalImageIndex + 1}`}
                  className="shadow-2xl seImg"
                  style={{
                    height: 'auto',
                    width: 'auto',
                    display: 'block',
                    objectFit: 'contain',
                    transform: `scale(${zoomLevel})`,
                    transformOrigin: zoomLevel > 1 ? 'top left' : 'center center',
                    maxWidth: '100%',
                    maxHeight: '98vh',
                    pointerEvents: 'none', // Prevent image drag, allow container drag
                  }}
                  onLoad={handleImageLoad}
                  draggable="false"
                  onFocus={(e) => {
                    sessionStorage.setItem('legitimate_modal_interaction', 'true');
                  }}
                />
              </div>
            </div>

            {/* All buttons moved outside the scrolling container - they stay fixed now */}

            {/* Close button - positioned relative to modal container (not image container) */}
            <button
              onClick={handleCloseModal}
              className="absolute top-4 right-4 z-10 bg-gray-400 bg-opacity-70 hover:bg-opacity-90 text-gray-200 rounded-full p-2 transition-all duration-200 clsBtn"
              aria-label="Close modal"
              onFocus={(e) => {
                sessionStorage.setItem('legitimate_modal_interaction', 'true');
              }}
            >
              <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <line x1="18" y1="6" x2="6" y2="18"></line>
                <line x1="6" y1="6" x2="18" y2="18"></line>
              </svg>
            </button>

            {/* Zoom controls - positioned near close button */}
            <div className="absolute top-4 right-16 z-10 flex gap-1">
              {/* Zoom Out (-) button */}
              <button
                onClick={handleZoomOut}
                disabled={zoomLevel <= 1}
                className={`bg-gray-400 bg-opacity-70 hover:bg-opacity-90 text-gray-200 rounded-full p-2 transition-all duration-200 ${
                  zoomLevel <= 1 ? 'opacity-50 cursor-not-allowed' : ''
                }`}
                aria-label="Zoom out"
                onFocus={(e) => {
                  sessionStorage.setItem('legitimate_modal_interaction', 'true');
                }}
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="5" y1="12" x2="19" y2="12"></line>
                </svg>
              </button>

              {/* Reset zoom button */}
              <button
                onClick={handleResetZoom}
                className="bg-gray-400 bg-opacity-70 hover:bg-opacity-90 text-gray-200 rounded-full px-2 py-1 transition-all duration-200 text-xs"
                aria-label={`Reset zoom (${Math.round(zoomLevel * 100)}%)`}
                onFocus={(e) => {
                  sessionStorage.setItem('legitimate_modal_interaction', 'true');
                }}
              >
                {Math.round(zoomLevel * 100)}%
              </button>

              {/* Zoom In (+) button */}
              <button
                onClick={handleZoomIn}
                disabled={zoomLevel >= maxZoom}
                className={`bg-gray-400 bg-opacity-70 hover:bg-opacity-90 text-gray-200 rounded-full p-2 transition-all duration-200 ${
                  zoomLevel >= maxZoom ? 'opacity-50 cursor-not-allowed' : ''
                }`}
                aria-label="Zoom in"
                onFocus={(e) => {
                  sessionStorage.setItem('legitimate_modal_interaction', 'true');
                }}
              >
                <svg xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <line x1="12" y1="5" x2="12" y2="19"></line>
                  <line x1="5" y1="12" x2="19" y2="12"></line>
                </svg>
              </button>
            </div>

            {/* Previous button - positioned relative to modal container (not image container) */}
            {message.imageLinks && message.imageLinks.length > 1 && (
              <button
                onClick={handlePrevImage}
                className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-600 hover:text-gray-800 text-3xl font-bold transition-all duration-200 bg-transparent bg-opacity-70 rounded-full px-3 py-1 nextBtnPnc"
                aria-label="Previous image"
                onFocus={(e) => {
                  sessionStorage.setItem('legitimate_modal_interaction', 'true');
                }}
              >
                &lt;
              </button>
            )}

            {/* Next button - positioned relative to modal container (not image container) */}
            {message.imageLinks && message.imageLinks.length > 1 && (
              <button
                onClick={handleNextImage}
                className="absolute right-4 top-1/2 transform -translate-y-1/2 text-gray-600 hover:text-gray-800 text-3xl font-bold transition-all duration-200 bg-opacity-70 bg-transparent rounded-full px-3 py-1 nextBtnPnc"
                aria-label="Next image"
                onFocus={(e) => {
                  sessionStorage.setItem('legitimate_modal_interaction', 'true');
                }}
              >
                &gt;
              </button>
            )}

            {/* Image counter - positioned relative to modal container (not image container) */}
            {message.imageLinks && message.imageLinks.length > 1 && (
              <div className="absolute bottom-4 left-1/2 transform -translate-x-1/2 text-gray-700 px-3 py-1 rounded-full text-sm bg-white bg-opacity-80">
                {modalImageIndex + 1} / {message.imageLinks.length}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Simple Feedback Modal */}
      {showFeedbackModal && (
        <SimpleFeedbackModal
          isOpen={showFeedbackModal}
          onClose={handleCloseFeedbackModal}
          onSubmit={handleFeedbackSubmit}
          feedbackType={feedbackType}
          chatRecordId={selectedChatRecordId}
          userId={
            sessionStorage.getItem("user_id") ||
            localStorage.getItem("user_id") ||
            userId ||
            ""
          }
        />
      )}
    </>
  );
};

export default FusoChatMessage;